// /verify-email/:token — auto-consumes an email-verification token on mount.
// Public route: works whether or not the visitor is logged in. If logged in we
// refresh the session so the verify banner clears.
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { AuthLayout } from "./auth-layout";
import { Alert, Button, Spinner } from "@/components/ui";
import { verifyEmail } from "@/api/auth";

type Phase = "verifying" | "success" | "error";

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const { status, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("verifying");
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current || !token) return;
    attempted.current = true;
    void (async () => {
      try {
        await verifyEmail(token);
        if (status === "authenticated") await refreshMe();
        setPhase("success");
      } catch {
        setPhase("error");
      }
    })();
  }, [token, status, refreshMe]);

  return (
    <AuthLayout
      title={t("Verifying your email")}
      subtitle={
        phase === "success"
          ? t("Your email is confirmed.")
          : phase === "error"
            ? t("We couldn't verify that link.")
            : t("One moment…")
      }
      footer={null}
    >
      {phase === "verifying" && (
        <div className="flex justify-center py-6 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {phase === "success" && (
        <div className="space-y-4">
          <Alert tone="info">{t("Thanks — your email address is now verified.")}</Alert>
          <Button
            size="lg"
            className="w-full"
            onClick={() => navigate(status === "authenticated" ? "/app" : "/login", { replace: true })}
          >
            {status === "authenticated" ? t("Go to the app") : t("Sign in")}
          </Button>
        </div>
      )}
      {phase === "error" && (
        <div className="space-y-4">
          <Alert>{t("This verification link is invalid or has expired.")}</Alert>
          <Button
            size="lg"
            variant="secondary"
            className="w-full"
            onClick={() => navigate(status === "authenticated" ? "/app" : "/login", { replace: true })}
          >
            {status === "authenticated" ? t("Go to the app") : t("Sign in")}
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
